'use client';

import { Stack } from '@mui/material';
import { useRouter } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { useGA } from '@/hooks/useGA';

import { Input } from './Input';
import { Textarea } from './Textarea';
import HexagonalButton from './bases/HexagonalButton';

type FormData = {
  firstName: string;
  lastName: string;
  email: string;
  subject: string;
  message: string;
};

export const ContactUsForm = () => {
  const router = useRouter();
  const { trackButtonClickEvent } = useGA();
  const {
    handleSubmit,
    control,
    watch,
    getValues,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      subject: '',
      message: '',
    },
  });

  const getContactLink = useCallback(() => {
    const { firstName, lastName, email, subject, message } = getValues();

    let str = `mailto:inquiries@lfg.inc?a=1`;
    if (subject) {
      str += `&subject=${encodeURIComponent(subject)}`;
    }

    if (message) {
      let content = '&body=';
      if (firstName) {
        content += encodeURIComponent(`Full Name: ${lastName} ${firstName}`);
      }
      content += encodeURIComponent('\n\n' + message);
      str += content;
    }

    if (email) {
      str += encodeURIComponent(`\n\n From: ${email}`);
    }

    return str;
  }, []);

  const onSubmit = () => {
    router.push(getContactLink());
  };

  return (
    <Stack gap={{ xs: 2, sm: 3 }} width={'100%'} height={'100%'} flexGrow={1}>
      <Stack width={'100%'} gap={3} alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
        <Input<FormData>
          rules={{ required: 'This field is required' }}
          control={control}
          name='firstName'
          title={'First Name*'}
          placeholder={'First Name'}
        />
        <Input<FormData>
          rules={{ required: 'This field is required' }}
          control={control}
          name='lastName'
          title={'Last Name*'}
          placeholder={'Last Name'}
        />
      </Stack>
      <Input<FormData>
        rules={{
          required: 'This field is required',
          pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email address' },
        }}
        control={control}
        name='email'
        title={'Email*'}
        placeholder={'example@email.com'}
      />
      <Input<FormData>
        rules={{ required: 'This field is required' }}
        control={control}
        name='subject'
        title={'Subject*'}
        placeholder={''}
      />
      <Textarea<FormData>
        rules={{ required: 'This field is required' }}
        disableResize
        rows={6}
        control={control}
        name='message'
        title={'Message*'}
        placeholder={'Some details on your project'}
      />

      <Stack width={'100%'} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
        <Stack width={'220px'}>
          <HexagonalButton
            label={'SEND MESSAGE'}
            onClick={() => {
              trackButtonClickEvent({ name: 'contact_us' });
              handleSubmit(onSubmit)();
            }}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
